/* eslint quote-props: 0 */
import _ from 'underscore';

import Settings from 'source/lib/settings_model';
import getUrlWithoutParams from 'source/lib/utils/get_url_without_params';

import twofactorBanned from './2f_banned.html';
import twofactorBannedUntil from './2f_banned_until.html';
import twofactorInvalidCredentials from './2f_invalid_credentials.html';
import twofactorLongCode from './2f_max_length.html';
import twofactorShortCode from './2f_min_length.html';
import twofactorUnauthorized from './2f_unauthorized.html';
import template404 from './404.html';

import accessChar from './access_char.html';
import accessCharCn from './access_char_cn.html';
import accessDeniedCreateNew from './access_denied_create_new.html';
import alreadyAuthorized from './already_authorized.html';
import authenticationFiled from './authentication_failed.html';
import banByIp from './ban_by_ip.html';
import captchaDeafult from './captcha_default.html';
import captchaInvalid from './captcha_invalid.html';
import charInvalid from './char_invalid.html';
import charInvalidCn from './char_invalid_cn.html';
import confirmPageTitle from './confirm_page_title.html';
import emailNotFound from './email_not_found.html';
import externalAccessDenied from './external_access_denied.html';
import externalVkBlocked from './external_vk_blocked.html';
import externalInvalidParameters from './external_invalid_parameters.html';
import externalInvalidParametersSubmit from './external_invalid_parameters_submit.html';
import externalXBLInvalidAccount from './external_xbl_invalid_account.html';
import externalTemporarilyUnavailable from './external_temporarily_unavailable.html';
import externalUnknownExternalAccount from './external_unknown_external_account.html';
import eula from './eula.html';
import subscribe from './subscribe.html';
import eulaRequiredError from './eula_required_error.html';
import forLinkProfile from './for_link_profile.html';
import gameAs from './game_as.html';
import invalidCredentials from './invalid_credentials.html';
import invalidPassword from './invalid_password.html';
import loginExist from './login_exist.html';
import loginTemporarilyUnavailable from './login_temporarily_unavailable.html';
import loginEmailCodeAttemptsReached from './login_email_code_attempts_reached.html';
import loginDomainDisabled from './login_domain_disabled.html';
import longName from './long_name.html';
import nameBanned from './name_banned.html';
import nameExist from './name_exist.html';
import notActivated from './not_activated.html';
import registrationTemporarilyUnavailable from './registration_temporarily_unavailable.html';
import restoreDeleteInProgress from './restore_delete_in_progress.html';
import restoreNotSuspended from './restore_not_suspended.html';
import serverTemporarilyUnavailable from './server_temporarily_unavailable.html';
import loginTemporarilyLocked from './login_temporarily_locked.html';
import loginRestrictedByLegalReasons from './login_restricted_by_legal_reasons.html';
import externalLoginRestrictedByLegalReasons from './external_login_restricted_by_legal_reasons.html';
import serverTimeOut from './server_time_out.html';
import serviceUnavailable from './service_unavailable.html';
import shortName from './short_name.html';
import startGame from './start_game.html';
import suggestedText from './suggested_text.html';
import uidExist from './uid_exist.html';
import unboundExternalInvalidParameters from './unbound_external_invalid_parameters.html';
import unboundNotYourAccount from './unbound_not_your_account.html';
import unboundSubmitText from './unbound_submit_text.html';
import titleDefault from './title_default.html';

class Messages {
    constructor() {
        this.setDict();
        Settings.on('update', this.setDict, this);
    }

    setDict() {
        const cnRealm = Settings.get('Realm.CurrentRealm') === 'cn360';
        const nameRequired = cnRealm ? accessCharCn : accessChar;
        const vkBlockedError = externalVkBlocked({
            passwordResetLinkStart: Settings.get('Authentication.PasswordResetUrl')
                ? `<a href="${Settings.get('Authentication.PasswordResetUrl')}">`
                : undefined,
            supportLinkStart: `<a href="${Settings.get('Realm.SupportCreateTicketUrl')}">`,
            externalName: Settings.get('Authentication.ExternalProvidersNames.vkontakte', 'VK.com'),
        });
        const brandName = Settings.get('Realm.BrandName');
        const companyName = Settings.get('Realm.CompanyName');

        const loginLockedError = loginTemporarilyLocked({
            lockTimeout: Settings.get('Authentication.LockTimeout', 5),
        });
        const loginRestrictedError = loginRestrictedByLegalReasons({
            teleportLink: Settings.get('Realm.ExternalTeleportLink'),
            supportURL: Settings.get('Realm.TeleportSupportUrl'),
        });
        const externalLoginRestrictedError = (options) => {
            const { externalName } = options;
            return externalLoginRestrictedByLegalReasons({
                teleportLink: Settings.get('Realm.ExternalTeleportLink'),
                supportURL: Settings.get('Realm.TeleportSupportUrl'),
                externalName,
            });
        };

        _.extend(this, {
            errors: {
                'common': {
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'vk_blocked': vkBlockedError,
                },

                // flow related errors
                'basic': {
                    'ban_by_ip': banByIp(),
                    'invalid_credentials': invalidCredentials(),
                    'login_temporarily_locked': loginLockedError,
                    'login_restricted_by_legal_reasons': loginRestrictedError,
                    'account_not_activated': notActivated(),
                    'login_email_code_temporarily_locked': loginEmailCodeAttemptsReached(),
                    'login_email_code_attempts_reached': loginEmailCodeAttemptsReached(),
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                },
                'restore-account': {
                    'eula_required': eulaRequiredError(),
                    'delete_in_progress': restoreDeleteInProgress(),
                    'not_suspended': restoreNotSuspended(),
                    'unexpected_error': serverTemporarilyUnavailable(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                },
                'confirmation': {
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'ban_by_ip': banByIp(),
                    'invalid_password': invalidPassword(),
                    'account_not_activated': notActivated(),
                    'login_restricted_by_legal_reasons': loginRestrictedError,
                },
                'external': {
                    'invalid_params': externalTemporarilyUnavailable(),
                    'access_denied': externalAccessDenied(),
                    'external_temporarily_unavailable': externalTemporarilyUnavailable(),
                    'error': externalTemporarilyUnavailable(),
                    'unknown_external_account': externalUnknownExternalAccount,
                    'external_login_restricted_by_legal_reasons': externalLoginRestrictedError,
                    'external_xbl_invalid_account': externalXBLInvalidAccount({
                        supportURL: Settings.get('Realm.SupportUrl'),
                    }),
                    'vk_blocked': vkBlockedError,
                },
                'external-registration': {
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'authentication_failed': authenticationFiled,
                    'spa_conflicted_login': loginExist({
                        authUrl: Settings.get('Authentication.LoginUrl'),
                    }),
                    'no_external_email': emailNotFound({
                        registrationUrl: Settings.get(
                            'Authentication.RegistrationUrl',
                            '/registration/',
                        ),
                    }),
                    'spa_bad_response': registrationTemporarilyUnavailable(),
                    'spa_internal_communication_error': registrationTemporarilyUnavailable(),
                    'spa_generic_conflict': registrationTemporarilyUnavailable(),
                    'spa_conflicted_uid': uidExist({
                        authUrl: Settings.get('Authentication.LoginUrl'),
                        brandName: Settings.get('Realm.BrandName'),

                    }),
                    'invalid_external_parameters': externalInvalidParameters({
                        authUrl: Settings.get('Authentication.LoginUrl'),
                    }),
                    'no_external_key': registrationTemporarilyUnavailable(),
                    'invalid_external_key': registrationTemporarilyUnavailable(),
                    'invalid_params': registrationTemporarilyUnavailable(),
                    'spa_conflicted_nickname': nameExist(),
                    'already_processed': serverTemporarilyUnavailable(),
                    'timeout': registrationTemporarilyUnavailable(),
                    'banned_ip': banByIp(),
                    'invalid_nickname': nameBanned(),
                    'error': registrationTemporarilyUnavailable(),
                },
                'external-bind': {
                    'already-authorized': alreadyAuthorized,
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'ban_by_ip': banByIp(),
                    'invalid_credentials': invalidCredentials(),
                    'account_not_activated': notActivated(),
                    'login_restricted_by_legal_reasons': loginRestrictedError,
                    'login_email_code_attempts_reached': loginEmailCodeAttemptsReached(),
                },
                'unbound': {
                    'already-authorized': alreadyAuthorized,
                    'error-access_denied': externalAccessDenied(),
                    'error-access_denied-submit-text': startGame(),
                    'error-access_denied-info-content': accessDeniedCreateNew,
                    'bind_basic-confirm_message': (data) => `${alreadyAuthorized(data)}${forLinkProfile(data)}`,
                    'submit-text': unboundSubmitText,
                    'info_content': unboundNotYourAccount,
                    'error-invalid_params': unboundExternalInvalidParameters(),
                    'error-invalid_params-submit-text': externalInvalidParametersSubmit,
                    'error-invalid_params-info-content': gameAs,
                    'invalid_password': invalidPassword(),
                    'ban_by_ip': banByIp(),
                    'invalid_credentials': invalidCredentials(),
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'account_not_activated': notActivated(),
                },

                // fields related errors
                'captcha': {
                    'default': captchaDeafult(),
                    'invalid': captchaInvalid(),
                },
                'nickname': {
                    'default': serviceUnavailable(),
                    'suggested_error': nameExist(),
                    'suggested_text': suggestedText(),
                },
                'login': {
                    'disabled_domain': loginDomainDisabled(),
                },

                // hints related errors
                'external-registration-hint': {
                    'description': nameRequired(Settings.get('General')),
                    'required': nameRequired(Settings.get('General')),
                    'banned': nameBanned(),
                    'invalid': cnRealm ? charInvalidCn() : charInvalid(),
                    'max_length': longName(),
                    'min_length': shortName(),
                    'exists': nameExist(),
                    'exists_error': nameExist(),
                    'exists_text': suggestedText(),
                },
                '2factor': {
                    'invalid': twofactorInvalidCredentials(),
                    'invalid_credentials': twofactorInvalidCredentials(),
                    'banned': twofactorBanned(),
                    'banned_until': twofactorBannedUntil,
                    'banned_new_attempts': loginEmailCodeAttemptsReached(),
                    'login_temporarily_unavailable': loginTemporarilyUnavailable(),
                    'max_length': twofactorLongCode(),
                    'min_length': twofactorShortCode(),
                    'server_not_answer': serverTemporarilyUnavailable(),
                    'server_timeout_answer': serverTimeOut(),
                    'login_restricted_by_legal_reasons': loginRestrictedError,
                    'unauthorized': twofactorUnauthorized({
                        authUrl: getUrlWithoutParams(
                            Settings.get('Authentication.LoginUrl'),
                            new URLSearchParams(window.location.search),
                            ['twofactor_method', 'external_status_state', 'external'],
                        ),
                    }),
                },
            },
            texts: {
                'external-registration': {
                    'eula': eula({
                        eulaUrl: Settings.get('Realm.UrlEULA'),
                        privacyUrl: Settings.get('Realm.UrlPrivacyPolicy'),
                    }),
                    'subscribe': subscribe({
                        brandName,
                        companyName,
                        adsAgreementUrl: Settings.get('Realm.UrlAdsAgreement'),
                        personalDataAgreementUrl: Settings.get('Realm.UrlPersonalDataAgreement'),
                    }),
                },
                'eula_lesta': eula({
                    eulaUrl: Settings.get('Realm.UrlEULALesta'),
                    privacyUrl: Settings.get('Realm.UrlPrivacyPolicyLesta'),
                    personalDataAgreementUrl: Settings.get('Realm.UrlPersonalDataAgreement'),
                }),
                'confirm_page_title': confirmPageTitle(),
                'base_title': `${brandName} ID`,
                'default_page_title': titleDefault(),
                '404_title': template404(),
            },
        });
    }
}

const messages = new Messages();

export default messages;
